import { SEOHead } from "@/components/SEOHead";
import { Layout } from "@/components/layout/Layout";
import { Link } from "react-router-dom";
import { Bot, Zap, BarChart3, CheckCircle, ArrowRight, Target, Brain, TrendingUp, Users, Database, Clock, Shield, Layers, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

const AISalesOps = () => {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "NoQ5 AI Sales Ops",
      "applicationCategory": "BusinessApplication",
      "description": "AI-powered sales operations automation. Replace manual RevOps work with autonomous agents that manage pipeline, forecasting, and CRM hygiene.",
      "url": "https://noq5.ai/ai-sales-ops",
      "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
      "publisher": { "@type": "Organization", "name": "NoQ5", "url": "https://noq5.ai" }
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        { "@type": "Question", "name": "What is AI Sales Ops?", "acceptedAnswer": { "@type": "Answer", "text": "AI Sales Ops uses artificial intelligence agents to automate sales operations tasks — pipeline management, data hygiene, forecasting, territory planning, and rep productivity tracking — without manual intervention from a Sales Ops or RevOps team." } },
        { "@type": "Question", "name": "Can AI replace Sales Ops?", "acceptedAnswer": { "@type": "Answer", "text": "AI doesn't replace Sales Ops people — it replaces the manual, repetitive work that consumes 60%+ of their time. With AI handling data hygiene, report generation, and pipeline audits, Sales Ops teams can focus on strategy and enablement." } }
      ]
    }
  ];

  return (
    <Layout>
      <SEOHead
        title="AI Sales Ops — Automate RevOps & Sales Operations | NoQ5"
        description="AI-powered sales operations automation. Replace manual pipeline management, data hygiene, and forecasting with autonomous AI agents. Built for RevOps teams."
        keywords="AI sales ops, RevOps automation, sales operations AI, AI RevOps, sales ops automation, revenue operations AI, automated sales ops, AI pipeline management"
        canonicalUrl="https://noq5.ai/ai-sales-ops"
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-background via-background to-primary/5">
        <div className="container mx-auto px-4 max-w-5xl text-center">
          <span className="inline-block px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-semibold mb-6">AI SALES OPS</span>
          <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6 leading-tight">
            Sales Ops Shouldn't Be<br /><span className="text-primary">Manual Labor</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            Your RevOps team spends 60% of their time on data hygiene, pipeline audits, and report generation. NoQ5 automates the operational grind so they can focus on strategy.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground" asChild>
              <Link to="/trial">Automate Sales Ops <ArrowRight className="ml-2 h-5 w-5" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* What AI Sales Ops Covers */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold text-center mb-12">What AI Automates in Sales Operations</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Database, title: "CRM Data Hygiene", desc: "Automatically detect and fix duplicate records, missing fields, outdated contacts, and inconsistent data." },
              { icon: BarChart3, title: "Pipeline Analytics", desc: "Real-time pipeline health dashboards powered by actual engagement data, not self-reported rep updates." },
              { icon: TrendingUp, title: "Revenue Forecasting", desc: "Evidence-based forecasts built on buyer signals, deal velocity, and historical win patterns — not gut feeling." },
              { icon: Target, title: "Territory Management", desc: "AI-optimized territory assignments based on rep capacity, deal complexity, and historical performance." },
              { icon: Users, title: "Rep Productivity", desc: "Automated activity tracking and coaching insights without requiring reps to log their own work." },
              { icon: RefreshCw, title: "Process Automation", desc: "Stage gates, approval workflows, and handoff processes that execute themselves based on deal signals." }
            ].map((item, i) => (
              <div key={i} className="bg-card p-6 rounded-xl border border-border">
                <item.icon className="h-10 w-10 text-primary mb-4" />
                <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                <p className="text-muted-foreground text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Before vs After */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold text-center mb-12">Sales Ops: Manual vs. AI-Powered</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-card p-8 rounded-xl border-2 border-destructive/30">
              <h3 className="text-xl font-bold text-destructive mb-6">❌ Manual Sales Ops</h3>
              <ul className="space-y-3">
                {["Weekly pipeline scrubs take 4+ hours", "Reports built manually in spreadsheets", "Data quality degrades between cleanups", "Forecasts revised constantly, never accurate", "Territory changes require weeks of analysis", "Rep onboarding takes 3-6 months"].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-muted-foreground"><span className="text-destructive mt-1">✗</span>{item}</li>
                ))}
              </ul>
            </div>
            <div className="bg-card p-8 rounded-xl border-2 border-primary/30">
              <h3 className="text-xl font-bold text-primary mb-6">✅ AI-Powered Sales Ops</h3>
              <ul className="space-y-3">
                {["Continuous pipeline hygiene, zero manual scrubs", "Real-time dashboards updated automatically", "Data quality maintained 24/7 by AI agents", "Forecasts accurate within 5-10% variance", "Territory optimization in minutes", "New reps productive from week 1"].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-muted-foreground"><CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />{item}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Related Pages */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold text-center mb-12">Explore AI Sales Ops Capabilities</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: "CRM AI Agent", link: "/crm-agent", desc: "The autonomous agent powering every Sales Ops automation." },
              { title: "Pipeline Management AI", link: "/pipeline-management-ai", desc: "AI-driven pipeline tracking and deal risk detection." },
              { title: "AI CRM Automation", link: "/ai-crm-automation", desc: "End-to-end CRM workflow automation with AI." }
            ].map((item, i) => (
              <Link key={i} to={item.link} className="bg-card p-6 rounded-xl border border-border hover:border-primary/50 transition-colors group">
                <h3 className="font-bold text-lg mb-2 group-hover:text-primary">{item.title}</h3>
                <p className="text-muted-foreground text-sm mb-3">{item.desc}</p>
                <span className="text-primary text-sm font-medium flex items-center gap-1">Learn more <ArrowRight className="h-4 w-4" /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 text-center max-w-3xl">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Let AI Handle Sales Ops</h2>
          <p className="text-xl opacity-90 mb-8">Free your RevOps team from manual work. Deploy AI agents that manage data hygiene, pipeline analytics, and forecasting autonomously.</p>
          <Button size="lg" variant="secondary" asChild>
            <Link to="/trial">Start Free Trial <ArrowRight className="ml-2 h-5 w-5" /></Link>
          </Button>
        </div>
      </section>
    </Layout>
  );
};

export default AISalesOps;
